import { ReactNode, createContext, useContext, useRef } from 'react';

type CloseCallback = () => void;

// Decides whether a popover in `otherZone` must close when one in `openedZone` opens.
export type ShouldClosePopover = (args: { openedZone?: string; otherZone?: string }) => boolean;

export type InfoPopoverGroupHandle = {
  notifyOpen: (id: string, zone?: string) => void;
  subscribe: (id: string, close: CloseCallback, zone?: string) => () => void;
  closeAll: () => void;
};

export const InfoPopoverGroupContext = createContext<InfoPopoverGroupHandle | null>(null);

// Marks the elements that open a popover, so click-away handlers can tell them from the surrounding area.
export const INFO_POPOVER_TRIGGER_PROPS = { 'data-info-popover-trigger': true } as const;
export const INFO_POPOVER_TRIGGER_SELECTOR = '[data-info-popover-trigger]';

const InfoPopoverZoneContext = createContext<string | undefined>(undefined);

const closeAllPopovers: ShouldClosePopover = () => true;

export function useInfoPopoverGroup(shouldClose: ShouldClosePopover = closeAllPopovers): InfoPopoverGroupHandle {
  const closersRef = useRef<Map<string, { close: CloseCallback; zone?: string }>>(new Map());
  const shouldCloseRef = useRef(shouldClose);
  shouldCloseRef.current = shouldClose;

  const handleRef = useRef<InfoPopoverGroupHandle>({
    notifyOpen(id: string, zone?: string) {
      closersRef.current.forEach(({ close, zone: otherZone }, closerId) => {
        if (closerId !== id && shouldCloseRef.current({ openedZone: zone, otherZone })) {
          close();
        }
      });
    },
    subscribe(id: string, close: CloseCallback, zone?: string) {
      closersRef.current.set(id, { close, zone });
      return () => {
        closersRef.current.delete(id);
      };
    },
    closeAll() {
      closersRef.current.forEach(({ close }) => close());
    },
  });

  return handleRef.current;
}

export type InfoPopoverGroupProps = {
  children: ReactNode;
  shouldClose?: ShouldClosePopover;
};

export function InfoPopoverGroup({ children, shouldClose }: InfoPopoverGroupProps) {
  const parentGroup = useInfoPopoverGroupContext();
  const group = useInfoPopoverGroup(shouldClose);

  if (parentGroup) {
    return <>{children}</>;
  }

  return <InfoPopoverGroupContext.Provider value={group}>{children}</InfoPopoverGroupContext.Provider>;
}

export type InfoPopoverZoneProps = {
  name: string;
  children: ReactNode;
};

export function InfoPopoverZone({ name, children }: InfoPopoverZoneProps) {
  return <InfoPopoverZoneContext.Provider value={name}>{children}</InfoPopoverZoneContext.Provider>;
}

export function useInfoPopoverGroupContext(): InfoPopoverGroupHandle | null {
  return useContext(InfoPopoverGroupContext);
}

export function useInfoPopoverZone(): string | undefined {
  return useContext(InfoPopoverZoneContext);
}
